import { ChevronLeft, ChevronRight, ImageOff } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import { flagLabel, fmtDateTime, fmtSar, labelOf } from "./coUtils";

export const POS_TONE = {
    matched: "text-[color:var(--success,#15803d)] bg-[color:var(--success-soft,rgba(21,128,61,0.08))]",
    not_found: "text-[color:var(--ink-muted)] bg-[color:var(--bg-surface-2)]",
    ambiguous: "text-[color:var(--warning-soft-fg)] bg-[color:var(--warning-soft)]",
    unavailable: "text-[color:var(--ink-faint)] bg-[color:var(--bg-surface-2)]",
};
const REVIEW_TONE = {
    pending: "bg-[color:var(--warning-soft)] text-[color:var(--warning-soft-fg)]",
    needs_follow_up: "bg-[color:var(--danger-soft,rgba(220,38,38,0.08))] text-[color:var(--danger)]",
    confirmed: "bg-[color:var(--success-soft,rgba(21,128,61,0.08))] text-[color:var(--success,#15803d)]",
    record_invalid: "bg-[color:var(--bg-surface-2)] text-[color:var(--ink-muted)]",
};
const chip = "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold";

/** One record in «السجلات» / «يحتاج مراجعة»: masked phone only, flags on the row. */
export function RecordRow({ rec, scope, ids, onOpen, showBranch }) {
    const { t, tn, lang, dir } = useI18n();
    const L = scope?.labels || {};
    const branch = (scope?.branches || []).find((b) => b.id === rec.branch_id);
    const phone = rec.phone_kind === "mobile" ? rec.phone_masked : t(`co_phone_kind_${rec.phone_kind}`);
    const rs = rec.review?.status;
    const m = rec.pos?.match || "unavailable";
    const flags = rec.review?.flags || [];
    const Chevron = dir === "rtl" ? ChevronLeft : ChevronRight;
    return (
        <li>
            <button type="button" data-testid={ids.row(rec.id)} onClick={() => onOpen(rec.id)}
                    className="group flex w-full items-center gap-2 px-1 py-3 text-start transition-[background-color] duration-150 hover:bg-[color:var(--brand-orange-soft)]">
                <span className="block min-w-0 flex-1 space-y-1">
                    <span className="flex flex-wrap items-baseline justify-between gap-x-2">
                        <span className="flex items-baseline gap-2 text-sm font-bold text-[color:var(--ink)]">
                            <span dir="ltr">#{rec.invoice_no}</span>
                            <span dir="ltr">{fmtSar(rec.value_sar, lang, { halalas: false })} {lang === "ar" ? "ر.س" : "SAR"}</span>
                        </span>
                        {rs ? (
                            <span data-testid={ids.rowReview(rec.id)} data-status={rs} className={`${chip} ${REVIEW_TONE[rs] || ""}`}>
                                {labelOf(L.review_statuses, rs, lang)}
                            </span>
                        ) : null}
                    </span>
                    <span className="block text-[11px] text-[color:var(--ink-faint)]">
                        {fmtDateTime(rec.occurred_at, lang)}{showBranch && branch ? ` · ${tn(branch)}` : ""}
                    </span>
                    <span className="block truncate text-[11px] text-[color:var(--ink-muted)]">
                        {labelOf(L.categories, rec.category, lang)} · {labelOf(L.outcomes, rec.outcome, lang)} · {labelOf(L.sources, rec.source, lang)}
                        {phone ? <span> · <span dir="ltr">{phone}</span></span> : null}
                    </span>
                    <span className="flex flex-wrap items-center gap-1">
                        <span data-match={m} className={`${chip} ${POS_TONE[m] || ""}`}>
                            {t(`co_rec_pos_${m}`)}{m === "matched" && rec.pos?.total_halalas != null ? ` · ${fmtSar(rec.pos.total_halalas, lang)}` : ""}
                        </span>
                        {rec.photo_status !== "stored" ? (
                            <span className={`${chip} text-[color:var(--danger)] bg-[color:var(--danger-soft,rgba(220,38,38,0.08))]`}>
                                <ImageOff className="h-3 w-3" />{t("co_rec_photo_none")}
                            </span>
                        ) : null}
                        {rec.duplicate_of && !rec.duplicate_dismissed ? <span className={`${chip} bg-[color:var(--bg-surface-2)] text-[color:var(--ink-muted)]`}>{t("co_badge_duplicate")}</span> : null}
                        {flags.length ? (
                            <span data-testid={ids.rowFlags(rec.id)} className="contents">
                                {flags.map((f) => (
                                    <span key={f} data-flag={f} className={`${chip} border border-[color:var(--warning-soft-fg)]/30 text-[color:var(--warning-soft-fg)]`}>
                                        {flagLabel(t, f)}
                                    </span>
                                ))}
                            </span>
                        ) : null}
                    </span>
                </span>
                <Chevron className="h-4 w-4 shrink-0 text-[color:var(--ink-faint)] transition-transform duration-150 group-hover:-translate-x-0.5" />
            </button>
        </li>
    );
}
